import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, House, Menu, Search } from 'lucide-react';
import type { Utilisateur } from '@/shared/auth/types';
import { enModeDemo } from '@/shared/supabase/client';
import { Avatar } from '@/shared/ui';
import { entreePourChemin, groupeDe } from '../navigation';
import { Cloche } from './Cloche';
import { SelecteurCompte } from './SelecteurCompte';

const surMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

export function Header({
  utilisateur,
  onOuvrirRecherche,
  onOuvrirMenu,
}: {
  utilisateur: Utilisateur;
  onOuvrirRecherche: () => void;
  onOuvrirMenu: () => void;
}) {
  const { pathname } = useLocation();
  const entree = entreePourChemin(pathname);
  const groupe = entree ? groupeDe(entree) : undefined;

  return (
    <header className="flex items-center gap-4 px-4 py-4 lg:px-7">
      <button
        type="button"
        onClick={onOuvrirMenu}
        className="grid size-[38px] place-items-center rounded-[10px] border border-border bg-surface text-muted lg:hidden"
        aria-label="Ouvrir la navigation"
      >
        <Menu className="size-[18px]" aria-hidden />
      </button>

      <nav
        aria-label="Fil d’Ariane"
        className="flex min-w-0 flex-1 items-center gap-1.5 text-[13px] text-muted"
      >
        <Link to="/" aria-label="Accueil" className="hover:text-ink">
          <House className="size-3.5" aria-hidden />
        </Link>
        {groupe ? (
          <span className="hidden items-center gap-1.5 sm:inline-flex">
            <ChevronRight className="size-3.5 text-faint" aria-hidden />
            {groupe.libelle}
          </span>
        ) : null}
        {entree ? (
          <>
            <ChevronRight className="size-3.5 text-faint" aria-hidden />
            <b className="truncate font-medium text-ink">{entree.libelle}</b>
          </>
        ) : null}
      </nav>

      <button
        type="button"
        onClick={onOuvrirRecherche}
        className="hidden w-[300px] items-center gap-2 rounded-[10px] border border-border bg-surface py-2 pr-2.5 pl-3 text-[13px] text-faint hover:border-primary-border md:flex"
      >
        <Search className="size-[18px]" aria-hidden />
        Rechercher…
        <kbd className="ml-auto rounded-md border border-border bg-surface-sunken px-1.5 text-[11px] text-muted">
          {surMac ? '⌘ K' : 'Ctrl K'}
        </kbd>
      </button>
      <button
        type="button"
        onClick={onOuvrirRecherche}
        className="grid size-[38px] place-items-center rounded-[10px] border border-border bg-surface text-muted md:hidden"
        aria-label="Rechercher"
      >
        <Search className="size-[18px]" aria-hidden />
      </button>

      <Cloche utilisateur={utilisateur} />
      {enModeDemo() ? (
        <SelecteurCompte utilisateur={utilisateur} />
      ) : (
        <Avatar
          nom={utilisateur.nom || utilisateur.email}
          photo={utilisateur.photo ?? utilisateur.partenaireLogo}
        />
      )}
    </header>
  );
}
